import * as amplitude from "@amplitude/analytics-browser";
import { FunctionComponent, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { CustomQuestionAnswersByQuestionId } from "src/schemas/CustomQuestionType";
import { formatNestedQuestionAsProps } from "src/services/formTemplate/customQuestion";
import { isNotNull } from "src/services/predicates";
import { Form } from "src/services/url/Parent";
import * as AF from "src/types/formTemplate";
import {
  useInsertPersonAnswerBankMutation,
  useSaveCustomQuestionAnswers,
} from "../api";
import { useCustomQuestionContext } from "../context/CustomQuestionContext";
import {
  computeDigestFromQuestionsAndAnswers,
  mapBankAnswersToFormAnswers,
} from "../helpers";
import { AnswerBankRecord } from "../list/schemas/AnswerBank";
import { AnswerBankEntryForm } from "./AnswerBankEntryForm";
import {
  formatEmptyInitialValues,
  formatInsertPersonAnswerBankPayload,
} from "./helpers";

export const CreateAnswerBankEntryForm: FunctionComponent = () => {
  const {
    organization,
    customQuestion,
    customQuestionType,
    formId,
    personId,
    sourceIdCloneIdMapping,
    setShowAnswerBank,
    answerBankRemoteData,
  } = useCustomQuestionContext();

  const { step = "" } = useParams();
  const navigate = useNavigate();

  const insertPersonAnswerBankEntry = useInsertPersonAnswerBankMutation();
  const upsertCustomQuestionAnswers = useSaveCustomQuestionAnswers();

  const toast = useAvelaToast({
    position: "bottom",
    containerStyle: { marginBottom: 16 },
    isClosable: true,
  });

  const initialValues = formatEmptyInitialValues(
    customQuestionType.custom_question_type_fields.map(
      (field) => field.question_id
    )
  );

  const fieldQuestions: AF.ClonedQuestion<AF.WithId>[] = useMemo(() => {
    return customQuestionType.custom_question_type_fields
      .map((field) => {
        return formatNestedQuestionAsProps(field.question);
      })
      .filter(isNotNull);
  }, [customQuestionType.custom_question_type_fields]);

  const onSubmitHandler = useCallback(
    async (values: CustomQuestionAnswersByQuestionId) => {
      try {
        amplitude.track("Save entry clicked", {
          cqt_name: customQuestion.question,
        });

        // Compute digest for the new entry
        const digest = computeDigestFromQuestionsAndAnswers(
          fieldQuestions,
          values
        );

        // Check if an entry with the same digest already exists
        let answerBankId: uuid | undefined;
        if (answerBankRemoteData.hasData()) {
          const existingRecord = answerBankRemoteData.data.find(
            (record: AnswerBankRecord) => record.digest === digest
          );

          if (existingRecord) {
            // Use existing entry instead of creating a new one
            answerBankId = existingRecord.answerBankId;
          }
        }

        // Create new entry if no duplicate was found
        if (!answerBankId) {
          const result = await insertPersonAnswerBankEntry({
            variables: {
              entry: formatInsertPersonAnswerBankPayload({
                answersByQuestionId: values,
                customQuestionTypeId: customQuestionType.id,
                fieldQuestions,
                personId,
              }),
            },
          });
          const newAnswerBankId =
            result.data?.insert_person_answer_bank_one?.id;
          if (!newAnswerBankId) {
            throw new Error("Failed to insert answer bank entry");
          }
          answerBankId = newAnswerBankId;
        }
        await upsertCustomQuestionAnswers({
          formId,
          customQuestion,
          answersByQuestionId: mapBankAnswersToFormAnswers(
            values,
            sourceIdCloneIdMapping
          ),
          answerBankId,
        });
        toast({ title: `${customQuestion.question} saved` });
        setShowAnswerBank(true);
        navigate(Form.edit(organization, formId, parseInt(step, 10)), {
          state: { questionId: customQuestion.id },
        });
      } catch (error) {
        console.error(error);
        toast({
          title: `Error saving ${customQuestion.question}`,
          description:
            "Please try again later or report the problem to our support team.",
          status: "error",
        });
      }
    },
    [
      answerBankRemoteData,
      insertPersonAnswerBankEntry,
      customQuestionType.id,
      fieldQuestions,
      personId,
      upsertCustomQuestionAnswers,
      formId,
      customQuestion,
      sourceIdCloneIdMapping,
      toast,
      setShowAnswerBank,
      navigate,
      organization,
      step,
    ]
  );

  return (
    <AnswerBankEntryForm
      initialValues={initialValues}
      onSubmitHandler={onSubmitHandler}
    />
  );
};
